import { Button, Form, Input, Select, Spin, Card, List, Tag, message } from 'antd';
import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ReactSVG } from 'react-svg';
import { useDispatch, useSelector } from 'react-redux';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
import BreadCrumb from '../breadcrumbs';
import FormTitleHeader from '../Header/FormTitleHeader';
import {
  doGetCompany,
  doCreateCompany,
  doUpdateCompany,
  doCreateSetupIntent,
  doListPaymentMethods,
} from '../../modules/company/stores/thunk';
import { getClients } from '../../modules/client/api';
import './Company.scss';

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || '');

const CompanyCardFormContent = ({ companyId, onCardSaved }) => {
  const stripe = useStripe();
  const elements = useElements();
  const dispatch = useDispatch();
  const [saving, setSaving] = useState(false);

  const handleSaveCard = async () => {
    if (!stripe || !elements) {
      message.error('Stripe not initialized');
      return;
    }

    const cardElement = elements.getElement(CardElement);
    if (!cardElement) {
      message.error('Please enter card details');
      return;
    }

    setSaving(true);
    try {
      const result = await dispatch(doCreateSetupIntent(companyId)).unwrap();
      const { clientSecret } = result;
      const { error: confirmError, setupIntent } = await stripe.confirmCardSetup(clientSecret, {
        payment_method: { card: cardElement },
      });

      if (confirmError) {
        throw new Error(confirmError.message);
      }

      if (setupIntent.status === 'succeeded') {
        message.success('Card saved successfully');
        cardElement.clear();
        if (onCardSaved) onCardSaved();
      }
    } catch (err) {
      message.error(err.message || 'Failed to save card');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{ marginTop: 16 }}>
      <div className="ant-form-item">
        <div className="ant-form-item-label">
          <label>Card Details</label>
        </div>
        <CardElement
          options={{
            hidePostalCode: true,
            classes: {
              base: 'StripeCardElement',
              complete: 'StripeCardElement--complete',
              empty: 'StripeCardElement--empty',
              focus: 'StripeCardElement--focus',
              invalid: 'StripeCardElement--invalid',
            },
          }}
          className="stripe-card-element"
        />
      </div>
      <Button type="primary" onClick={handleSaveCard} loading={saving} disabled={!stripe}>
        Save Card
      </Button>
    </div>
  );
};

const CompanyCardForm = ({ companyId, onCardSaved }) => (
  <Elements stripe={stripePromise}>
    <CompanyCardFormContent companyId={companyId} onCardSaved={onCardSaved} />
  </Elements>
);

const CompanyPage = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [form] = Form.useForm();
  let { id } = useParams();
  const isFetching = useSelector((state) => state.company.isFetching);
  const status = useSelector((state) => state.company.status);
  const action = useSelector((state) => state.company.action);
  const companyDetail = useSelector((state) => state.company.companyDetail);
  const paymentMethods = useSelector((state) => state.company.paymentMethods);
  const [clients, setClients] = useState([]);
  const [fetchingClients, setFetchingClients] = useState(false);
  const breadCrumbs = [
    { text: 'COMPANIES', url: '/companies' },
    { text: id ? 'UPDATE COMPANY' : 'CREATE NEW COMPANY' },
  ];

  const loadClients = useCallback(async () => {
    setFetchingClients(true);
    try {
      const { response, error } = await getClients({ take: 10000, isActive: true });
      if (!error) {
        setClients(response.users || []);
      }
    } finally {
      setFetchingClients(false);
    }
  }, []);

  useEffect(() => {
    loadClients();
  }, [loadClients]);

  useEffect(() => {
    if (['create', 'update'].includes(action) && status === 'succeeded') {
      navigate('/companies');
    }
  }, [isFetching]);

  useEffect(() => {
    if (id) {
      dispatch(doGetCompany(id));
      dispatch(doListPaymentMethods(id));
    }
  }, [id]);

  useEffect(() => {
    if (companyDetail) {
      const memberIds = (companyDetail.clients || [])
        .filter((c) => c.id !== companyDetail.ownerClientId)
        .map((c) => c.id);
      form.setFieldsValue({
        name: companyDetail.name,
        ownerClientId: companyDetail.ownerClientId,
        clientIds: memberIds,
      });
    } else {
      form.resetFields();
    }
  }, [companyDetail]);

  const handleSubmit = (values) => {
    if (companyDetail) {
      dispatch(doUpdateCompany({ ...values, id: companyDetail.id }));
    } else {
      dispatch(doCreateCompany(values));
    }
  };

  const handleCardSaved = () => {
    if (id) {
      dispatch(doListPaymentMethods(id));
    }
  };

  const cardBrandLabel = (brand) => {
    const map = { visa: 'Visa', mastercard: 'Mastercard', amex: 'American Express' };
    return map[brand] || brand;
  };

  const filterOption = (input, option) => (option?.label ?? '').toLowerCase().includes(input.toLowerCase());

  // Clients available for selection: unassigned OR already in this company
  const availableClients = clients.filter((c) => !c.companyId || c.companyId === companyDetail?.id);

  return (
    <Spin spinning={isFetching}>
      <div className="Content CompanyPage">
        <BreadCrumb breadCrumbs={breadCrumbs} />

        <div className="FormContent">
          <FormTitleHeader title={id ? 'Update Company' : 'Create New Company'} />
          <div className="CompanyInfo">
            <Form
              form={form}
              scrollToFirstError
              layout="vertical"
              requiredMark={false}
              colon={false}
              onFinish={handleSubmit}
            >
              <Form.Item label="Company Name" name="name" rules={[{ required: true, message: 'Required' }]}>
                <Input size="large" placeholder="Company Name" />
              </Form.Item>
              <Form.Item label="Owner Client" name="ownerClientId" rules={[{ required: true, message: 'Required' }]}>
                <Select
                  size="large"
                  showSearch
                  placeholder="Select a client"
                  filterOption={filterOption}
                  loading={fetchingClients}
                  options={clients.map((c) => ({
                    label: `${c.name || 'N/A'} (${c.email})${c.companyId ? ' - In company' : ''}`,
                    value: c.id,
                    disabled: c.companyId && c.companyId !== (companyDetail?.id || 0),
                  }))}
                />
              </Form.Item>
              {id && (
                <Form.Item label="Member Clients" name="clientIds">
                  <Select
                    mode="multiple"
                    size="large"
                    className="select-full-size"
                    showSearch
                    placeholder="Select member clients"
                    filterOption={filterOption}
                    loading={fetchingClients}
                    options={availableClients
                      .filter((c) => c.id !== companyDetail?.ownerClientId)
                      .map((c) => ({
                        label: `${c.name || 'N/A'} (${c.email})`,
                        value: c.id,
                      }))}
                  />
                </Form.Item>
              )}
            </Form>
          </div>
          <div className="FormFooter">
            <Button size="large" className="ButtonCancel" onClick={() => navigate('/companies')}>
              Cancel
            </Button>
            <Button size="large" className="CreateButton ButtonPrimary" type="primary" onClick={() => form.submit()}>
              {id ? 'Update' : 'Create'}
              <ReactSVG className="icon" src="/icons/done.svg" />
            </Button>
          </div>
        </div>

        {id && (
          <div className="FormContent" style={{ marginTop: 24 }}>
            <FormTitleHeader title="Payment Methods" />
            {!companyDetail?.stripeCustomerId ? (
              <p style={{ color: '#999', padding: '0 24px' }}>
                Cannot add cards until a Stripe customer is created. Save the company first.
              </p>
            ) : (
              <div style={{ padding: '0 24px 24px' }}>
                <CompanyCardForm companyId={Number(id)} onCardSaved={handleCardSaved} />
                {paymentMethods.length > 0 && (
                  <List
                    style={{ marginTop: 24 }}
                    header={
                      <div>
                        <strong>Saved Cards</strong>
                      </div>
                    }
                    dataSource={paymentMethods}
                    renderItem={(pm) => (
                      <List.Item>
                        <Card style={{ width: '100%' }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <div>
                              <Tag color="blue">{cardBrandLabel(pm.brand)}</Tag>
                              <span>&bull;&bull;&bull;&bull; {pm.last4}</span>
                            </div>
                            <span style={{ color: '#999' }}>
                              Expires {pm.expMonth}/{pm.expYear}
                            </span>
                          </div>
                        </Card>
                      </List.Item>
                    )}
                  />
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </Spin>
  );
};

export default CompanyPage;
